/**
 * The user-tunable half of the theme: an accent hue and two appearance presets.
 *
 * `tokens.css` ships the design and the 50 themes around it — five surface
 * presets by five contrast presets, per mode, all generated. This file only
 * names the choices and writes them to the document. Every color still
 * resolves in CSS, so a mode flip re-resolves on its own and there is nothing
 * here to keep in sync.
 *
 * The presets are DISCRETE on purpose. Legibility is a relationship between a
 * fill and the thing drawn on it, and no expression CSS can evaluate preserves
 * it — there is no contrast function in `calc()`. So the safe values are
 * solved ahead of time and the control picks between them. A continuous slider
 * could only be made safe by clamping it, and a clamped slider is one whose
 * travel stops doing anything before it stops moving.
 */

/**
 * The pickable accents. Every name has `[data-accent]` blocks in `tokens.css`
 * for both modes and all five surface presets.
 *
 * This is deliberately NOT the entity palette, and the two must not be merged
 * back together. A palette name is persisted on rows and is permanent — it can
 * never be removed once shipped. An accent is a product decision about how
 * many themes to offer, and shortening the list has to stay cheap. They also
 * answer different questions: the palette needs seventeen hues far enough
 * apart to tell one team from another at a glance, while an accent only has to
 * look good as the single brand color of an entire interface.
 *
 * Red is deliberately not offered: the accent paints selected states and
 * primary actions, and red is the one hue the app reserves for destructive —
 * a red accent makes every confirm button read as a delete. A previously
 * stored "red" fails {@link isAccentColor} and falls back to the shipped
 * default.
 */
export const ACCENT_COLORS = [
  "emerald",
  "indigo",
  "amber",
  "violet",
  "cyan",
] as const;

export type AccentColor = (typeof ACCENT_COLORS)[number];

const ACCENT_SET: ReadonlySet<string> = new Set(ACCENT_COLORS);

/**
 * Whether a string names a pickable accent. Stored preferences arrive as
 * free-form strings, so this is the only sanctioned narrowing — never `as`.
 */
export function isAccentColor(
  value: string | null | undefined,
): value is AccentColor {
  return value != null && ACCENT_SET.has(value);
}

/**
 * How light the surfaces sit, dimmest to brightest, in both modes.
 *
 * The travel is not symmetric between the modes and cannot be: light mode's
 * page starts a hair off white with the panels already at white, so nearly all
 * of its range is downward, while dark mode moves freely both ways. What the
 * two share is that the ramp keeps its proportions at every stop. The
 * hairlines are solved against whatever the fills settle at, so as the fills
 * converge the hairlines take over the separation the fills gave up.
 */
export const SURFACE_PRESETS = [
  "dimmest",
  "dim",
  "default",
  "bright",
  "brightest",
] as const;

export type SurfacePreset = (typeof SURFACE_PRESETS)[number];

/**
 * How hard text reads against whatever the surfaces settled on.
 *
 * These are contrast TARGETS, not lightness values, and that is the whole
 * safety property: no combination of the two controls can produce something
 * unreadable, because the text is solved against the surface rather than moved
 * independently of it.
 *
 * The middle three names are the CSS `prefers-contrast` keywords, so the
 * control can seed itself from the operating system's own setting.
 */
export const CONTRAST_PRESETS = [
  "least",
  "less",
  "default",
  "more",
  "most",
] as const;

export type ContrastPreset = (typeof CONTRAST_PRESETS)[number];

/**
 * A theme key per mode. Both halves are stored, always — the mode picks which
 * one is read, so switching appearance never has to re-decide anything.
 */
/* eslint-disable no-restricted-syntax -- The shipped design's own source
   colours. They seed a theme rather than being painted by one, so no semantic
   token can name them without the input depending on its own output. */
/**
 * The shipped design AS A SEED, so every mode always has one.
 *
 * Light is read off `tokens.css` and verified to resolve back to it (worst
 * 0.94 L). Dark runs the other way: it is the seed the spine's dark values
 * were READ FROM, so the two agree by construction. Notion's canvas (#191919)
 * under a near-white ink (#ededed, Lc 96 on the page): Notion's own ink
 * (#d9d9d9, Lc 82) is the softest body in the reference set — Linear, GitHub,
 * Vercel and shadcn all draw theirs at Lc 96–104 — and at the density of a
 * table grid it read as dull rather than calm. The `gpt-notion` catalog entry
 * keeps the faithful pair. That is what lets Default be a theme like any other —
 * with an Accent, a Background, a Foreground and a Contrast a user can edit —
 * instead of the one choice that has nothing behind it. The pre-Notion dark
 * design lives on as the hidden `legacy-default` catalog entry.
 */
export const SHIPPED_THEME_SEEDS: Record<ThemeColorMode, ThemeColorSeed> = {
  light: {
    accent: "#3168dd",
    background: "#f9f9f9",
    foreground: "#0a0a0a",
    neutralPrimary: true,
  },
  dark: {
    accent: "#77a3f9",
    background: "#191919",
    foreground: "#ededed",
    neutralPrimary: true,
  },
};
/* eslint-enable no-restricted-syntax */

/**
 * The seed a theme key stands for. `null` is the shipped design.
 *
 * A theme that ships only one half falls back to the shipped seed for the
 * other, so a mode is never left without colours to edit.
 */
export function themeSeedFor(
  key: string | null,
  mode: ThemeColorMode,
): ThemeColorSeed {
  return namedThemeSeed(key, mode) ?? SHIPPED_THEME_SEEDS[mode];
}

export interface ThemeSelection {
  light: string | null;
  dark: string | null;
}

/** Coerces a stored key, dropping one the catalog no longer carries. */
export function resolveThemeSelection(
  light: string | null | undefined,
  dark: string | null | undefined,
): ThemeSelection {
  return {
    light: isNamedThemeKey(light) ? light : null,
    dark: isNamedThemeKey(dark) ? dark : null,
  };
}

export interface ThemeAppearance {
  /** `null` keeps the shipped near-black primary. */
  accent: AccentColor | null;
  /**
   * The chosen theme PER MODE — `null` in a half means the shipped design.
   *
   * A catalog theme is authored as a pair, but which half paints in light and
   * which in dark are separate choices, because they are separate rooms: the
   * reference implementation offers a Light theme and a Dark theme side by
   * side, and a user may want Notion by day and something else by night.
   */
  theme: ThemeSelection;
  /** Optional free-color seeds. Each mode remains independently tunable. */
  custom: ThemeColorSeeds;
  /**
   * Whether a catalog palette's corner radius paints, and whether its shadows
   * do. Two flags because they are two decisions: a palette's roundness and its
   * drop are independent, and squaring the corners of a soft theme is a thing
   * someone may want without also flattening every shadow.
   *
   * Both opt-in. Colors are what a theme is picked for; these move every corner
   * and every drop in the product at once.
   */
  themeRadius: boolean;
  themeShadows: boolean;
  /**
   * The EDITABLE seed per mode. Always paints; a theme pick seeds it.
   *
   * `null` in a half means "untouched since the theme was chosen", which is
   * what lets a palette's stated refinements still apply and its radius and
   * shadows still be on offer. The first edit makes the half the user's.
   */
}

export const DEFAULT_THEME_APPEARANCE: ThemeAppearance = {
  accent: null,
  theme: { light: null, dark: null },
  themeRadius: false,
  themeShadows: false,
  custom: EMPTY_THEME_COLOR_SEEDS,
};

/**
 * Writes the appearance to `<html>`.
 *
 * That element is the single write target on purpose: `:root` and `.dark` are
 * both selectors on it, so the preset attributes compose with either without a
 * "which mode am I in" branch, and `data-accent` sits on the same element the
 * accent blocks are written against.
 *
 * A default REMOVES its attribute rather than writing the name, so an
 * untouched app leaves no trace on the document and the authored cascade is
 * what a reader sees in devtools. The generator omits that cell for the same
 * reason — `:root` and `.dark` already say it.
 */
export function applyThemeAppearance(
  appearance: ThemeAppearance,
  mode: ThemeColorMode = "light",
  options?: CustomThemeOptions,
): void {
  const root = document.documentElement;

  // Dropped while a catalog theme paints, not just overridden: every token the
  // accent blocks write is one the seed also writes, so the inline values would
  // win anyway — but leaving the attribute on says the accent is doing something
  // when it is not, to anyone reading the document.
  setOrRemove(
    root,
    "data-accent",
    appearance.accent,
  );
  // No `data-surface` / `data-contrast`: a seed now paints in EVERY case, and
  // an inline custom property outranks any block, so those attributes could
  // only ever describe a cascade nothing reads. The preset matrix in
  // `tokens.css` is dead with them and is deleted separately.
  applyCustomTheme(
    root,
    mode,
    activeThemeSeed(appearance, mode),
    activeThemeOverrides(appearance, mode),
    options,
  );
  applyThemeShape(root, appearance, mode);
}

/**
 * A palette's stated values apply while its seed is UNEDITED. Once a user
 * changes a colour the seed is theirs, and a palette author's refinement
 * would be outranking the choice they just made.
 */
function activeThemeOverrides(
  appearance: ThemeAppearance,
  mode: ThemeColorMode,
): ThemeOverrides | undefined {
  return appearance.custom[mode] === null
    ? namedThemeOverrides(appearance.theme[mode], mode)
    : undefined;
}

/**
 * The token literals an appearance paints in a mode — the same resolution
 * `applyThemeAppearance` writes to the document, computed without touching it.
 *
 * This is the sanctioned read for a surface CSS custom properties cannot
 * reach (a canvas fill, an embedded map's styler): derive from the appearance
 * the way the document does, never probe the painted DOM. `null` only when a
 * stored seed fails to parse — callers fall back to their own resting palette.
 */
export function resolveAppearanceTokens(
  appearance: ThemeAppearance,
  mode: ThemeColorMode,
  options?: CustomThemeOptions,
): CustomThemeProperties | null {
  return resolveCustomTheme(
    mode,
    activeThemeSeed(appearance, mode),
    activeThemeOverrides(appearance, mode),
    options,
  );
}

/**
 * Writes a palette's radius and shadows, or clears them.
 *
 * `--radius` is one value because the whole scale derives from it, so a single
 * write reshapes every corner. The shadows are recomposed per surface rather
 * than copied, which is what keeps the elevation ladder: see `themeShadowCss`.
 */
export function applyThemeShape(
  target: HTMLElement,
  appearance: ThemeAppearance,
  mode: ThemeColorMode,
): void {
  const theme =
    appearance.custom[mode] === null ? namedTheme(appearance.theme[mode]) : null;
  const radius = appearance.themeRadius ? theme?.radius : undefined;
  const shadow = appearance.themeShadows ? (theme?.shadow?.[mode] ?? null) : null;

  if (radius) target.style.setProperty("--radius", radius);
  else target.style.removeProperty("--radius");

  for (const surface of THEME_SHADOW_SURFACES) {
    if (shadow) {
      target.style.setProperty(
        `--${surface}-shadow`,
        themeShadowCss(shadow, surface),
      );
    } else {
      target.style.removeProperty(`--${surface}-shadow`);
    }
  }
}

/**
 * The seed painting in a mode, or `null` for the shipped design.
 *
 * Custom outranks a named theme so that picking Custom does not have to clear
 * the named one — both are retained, exactly as the seeds are retained while
 * inactive. A named half may legitimately be absent (a theme shipping only
 * one), and so may an unauthored custom half; both fall through to `null`,
 * which paints the shipped design rather than an error state.
 */
export function activeThemeSeed(
  appearance: ThemeAppearance,
  mode: ThemeColorMode,
): ThemeColorSeed {
  // ONE editable seed per mode, and it always paints. Picking a theme SEEDS
  // it; the key is remembered so the picker can show what seeded it, but from
  // that moment the seed is the source of truth. That is what makes Accent,
  // Background, Foreground and Contrast editable under EVERY theme rather than
  // only under a "Custom" one — the shape the reference application has.
  const seed =
    appearance.custom[mode] ?? themeSeedFor(appearance.theme[mode], mode);
  // The workspace accent still overrides where one is chosen. Left unset, the
  // seed's own accent paints — a catalog palette's is the theme, and the
  // shipped seed carries `neutralPrimary` so Default keeps its near-black
  // button rather than turning blue.
  return appearance.accent === null
    ? seed
    : { ...seed, accent: accentSeedColor(appearance.accent, mode) };
}




function setOrRemove(
  root: HTMLElement,
  attribute: string,
  value: string | null,
): void {
  if (value === null) root.removeAttribute(attribute);
  else root.setAttribute(attribute, value);
}
import { oklchToHex } from "./color-convert.ts";
import {
  applyCustomTheme,
  DEFAULT_THEME_COLOR_SEEDS,
  EMPTY_THEME_COLOR_SEEDS,
  resolveCustomTheme,
  type CustomThemeOptions,
  type CustomThemeProperties,
  type ThemeColorMode,
  type ThemeColorSeed,
  type ThemeColorSeeds,
  type ThemeOverrides,
} from "./custom-theme.ts";
import {
  isNamedThemeKey,
  namedTheme,
  namedThemeOverrides,
  namedThemeSeed,
  THEME_SHADOW_SURFACES,
  themeShadowCss,
} from "./theme-catalog.ts";

/**
 * Each accent's per-mode SOLID tone, as the hex a custom-scheme seed carries.
 * The values are the spine's `ACCENTS` solids (`scripts/theme-spine.ts`) —
 * the same colors the `[data-accent]` blocks paint `--primary` with — so a
 * custom scheme under a named accent resolves the identical hue the stock
 * themes show. Keep the two lists in step when an accent is added.
 */
const ACCENT_SEED_TONES: Record<
  AccentColor,
  Record<ThemeColorMode, { l: number; c: number; h: number }>
> = {
  emerald: {
    light: { l: 52, c: 0.17, h: 163 },
    dark: { l: 52, c: 0.17, h: 163 },
  },
  indigo: {
    light: { l: 56, c: 0.22, h: 276.9 },
    dark: { l: 58, c: 0.22, h: 276.9 },
  },
  amber: {
    light: { l: 56, c: 0.19, h: 62 },
    dark: { l: 57, c: 0.19, h: 62 },
  },
  violet: {
    light: { l: 56, c: 0.23, h: 297 },
    dark: { l: 58, c: 0.23, h: 297 },
  },
  cyan: {
    light: { l: 53.5, c: 0.17, h: 232 },
    dark: { l: 54, c: 0.17, h: 232 },
  },
};

/**
 * The hex a custom seed's accent takes under a named accent — or under the
 * Default (null), the shipped seed accent for the mode. This is the bridge
 * that lets the accent live OUTSIDE the custom theme: picking an accent
 * rewrites the seeds through this, so stock and custom schemes answer the
 * one accent choice together.
 */
export function accentSeedColor(
  accent: AccentColor | null,
  mode: ThemeColorMode,
): string {
  if (accent === null) return DEFAULT_THEME_COLOR_SEEDS[mode].accent;
  return oklchToHex(ACCENT_SEED_TONES[accent][mode]);
}
